import { Injectable } from '@angular/core';
import { Observable, throwError, of, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { LocationService } from '../../services/location.service';
import {
  formatDateTimeWithTimezone,
  formatLocalDateTime,
  parseOrdersDisplayDateTime,
} from '../../shared/utils/date.util';
import { OrderUtils } from '../utils/order.utils';
import { familyTagsOf } from '../../shared/utils/family-tags.util';
import {
  ApiOrder,
  Order,
  OrderStatus,
  OrderStats,
  WashType,
  StatusFilters,
  WashTypeFilters,
  PaymentModeFilters,
  ApiOrderStatistics,
} from '../models/order.models';
import {
  mapApiStatisticsToOrderStats,
  mapPaymentModeFiltersToApiValues,
  PREPAID_FILTER_WASH_TYPES,
} from '../constants/orders.constants';

/** "12.50" / 12.5 → 12.5; anything else → null. */
function toAmountOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export interface RefreshOrdersOptions {
  resetPage?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  /**
   * Real svc-order list routes only. `order/list` / `order/get-all` / `orders` were never
   * routes — they fell through to `GET /order/:id` and answered 400 "Id must be a valid GUID",
   * turning one bad request into a cascade of misleading errors.
   */
  private readonly orderEndpoints = ['order/get-all-by-user', 'order'];

  private readonly orderStatuses: OrderStatus[] = [
    { label: 'Completed', value: 'completed', color: 'green' },
    { label: 'In Progress', value: 'in_progress', color: 'blue' },
    { label: 'Scheduled', value: 'scheduled', color: 'gray' },
    { label: 'Canceled', value: 'canceled', color: 'red' },
  ];

  private readonly washTypes: WashType[] = [
    { label: 'Monthly Membership', value: 'monthly_membership', color: 'purple' },
    { label: 'Single Wash', value: 'single_wash', color: 'orange' },
    { label: 'prepaid_wash_booking', value: 'prepaid_wash_book', color: 'blue' },
  ];

  // Subject to emit refresh events for orders components
  private refreshOrdersSubject = new Subject<RefreshOrdersOptions | void>();
  public refreshOrders$ = this.refreshOrdersSubject.asObservable();

  /**
   * Fired when admin/staff orders page receives payment success on the user Pusher channel
   * (`order-{userId}`). The notification modal may subscribe on `order-{locationId}` only, so it
   * uses this stream to complete the payment-link flow when those channels differ.
   */
  private paymentSucceededSubject = new Subject<{ orderId: string }>();
  readonly paymentSucceeded$ = this.paymentSucceededSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private locationService: LocationService
  ) {}

  /**
   * Trigger refresh of orders table and stats
   */
  refreshOrders(options?: RefreshOrdersOptions): void {
    this.refreshOrdersSubject.next(options);
  }

  /**
   * Emitted from admin/staff orders when Pusher reports payment success on `order-{userId}`.
   * The notification modal listens so payment-link completion works when events are only on the user channel.
   */
  emitPaymentSucceeded(orderId: string): void {
    if (!orderId) return;
    this.paymentSucceededSubject.next({ orderId: String(orderId).trim() });
  }

  /**
   * Load orders from the API with pagination, search, and filtering
   * @param page - Page number for pagination
   * @param limit - Number of items per page
   * @param searchTerm - Search term for text filtering
   * @param filterValue - Location or period filter value (for backward compatibility)
   * @param washTypeFilters - Wash type filters (single_wash, monthly_membership)
   * @param statusFilters - Status filters (pending, completed, cancelled, scheduled, in_progress)
   * @param sourceFilters - Source filters (wayCom, scanAndGo, wayApp, website)
   * @param locationId - Explicit location ID filter
   * @param periodFilter - Explicit period filter
   * @param useTunnelToken - Whether to use tunnel auth token
   * @param laneId - Optional lane ID filter
   * @param paymentModeFilters - Payment mode multi-select filters
   * @param packageIds - Selected package IDs for multi-select Package filter
   * @param discountApplied - Discount Applied filter (`all` / `none` / `any` / discount UUID)
   */
  loadOrders(
    page = 1,
    limit = 10,
    searchTerm = '',
    filterValue = '',
    washTypeFilters?: WashTypeFilters,
    statusFilters?: StatusFilters,
    sourceFilters?: { wayCom: boolean; scanAndGo: boolean; wayApp: boolean; website: boolean },
    locationId?: string,
    periodFilter?: string,
    useTunnelToken = false,
    laneId?: string,
    paymentModeFilters?: PaymentModeFilters,
    packageIds?: string[],
    discountApplied?: string
  ): Observable<{ orders: Order[]; totalItems: number }> {
    return this.tryEndpoints(
      this.orderEndpoints,
      page,
      limit,
      searchTerm,
      filterValue,
      washTypeFilters,
      statusFilters,
      sourceFilters,
      locationId,
      periodFilter,
      useTunnelToken,
      laneId,
      paymentModeFilters,
      packageIds,
      discountApplied
    );
  }

  /** Download filtered orders CSV (`order/download/csv`) with list-compatible filters. */
  downloadOrdersCsv(
    startDate: string,
    endDate: string,
    searchTerm = '',
    washTypeFilters?: WashTypeFilters,
    statusFilters?: StatusFilters,
    sourceFilters?: { wayCom: boolean; scanAndGo: boolean; wayApp: boolean; website: boolean },
    locationId?: string,
    laneId?: string,
    paymentModeFilters?: PaymentModeFilters,
    packageIds?: string[],
    discountApplied?: string
  ): Observable<Blob> {
    const apiParams: Record<string, string | number | boolean> = {
      start_date: startDate,
      end_date: endDate,
      filter_by: 'custom',
    };

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) apiParams['search'] = trimmedSearch;
    if (locationId) apiParams['location_id'] = locationId;

    const normalizedLaneId = laneId?.trim();
    if (normalizedLaneId) apiParams['lane_id'] = normalizedLaneId;

    this.addWashTypeFiltersToObject(apiParams, washTypeFilters);
    this.addStatusFiltersToObject(apiParams, statusFilters);
    this.addSourceFiltersToObject(apiParams, sourceFilters);
    this.addPaymentModeFiltersToObject(apiParams, paymentModeFilters);
    this.addPackageFiltersToObject(apiParams, packageIds);
    this.addDiscountAppliedFilterToObject(apiParams, discountApplied);

    return this.apiService.getBlob('order/download/csv', apiParams);
  }

  /**
   * Try multiple endpoints until one succeeds
   */
  private tryEndpoints(
    endpoints: string[],
    page = 1,
    limit = 10,
    searchTerm = '',
    filterValue = '',
    washTypeFilters?: WashTypeFilters,
    statusFilters?: StatusFilters,
    sourceFilters?: { wayCom: boolean; scanAndGo: boolean; wayApp: boolean; website: boolean },
    locationId?: string,
    periodFilter?: string,
    useTunnelToken = false,
    laneId?: string,
    paymentModeFilters?: PaymentModeFilters,
    packageIds?: string[],
    discountApplied?: string
  ): Observable<{ orders: Order[]; totalItems: number }> {
    if (endpoints.length === 0) {
      return throwError(() => new Error('All endpoints failed'));
    }

    const [currentEndpoint, ...remainingEndpoints] = endpoints;

    // Build query parameters as object (same pattern as order/statistics API)
    const apiParams: Record<string, any> = {
      page: page.toString(),
      limit: limit.toString(),
    };

    if (searchTerm) {
      apiParams['search'] = searchTerm;
    }

    // Handle explicit location and period parameters (new way)
    if (locationId) {
      apiParams['location_id'] = locationId;
    }
    const normalizedLaneId = laneId != null ? String(laneId).trim() : '';
    if (normalizedLaneId) {
      apiParams['lane_id'] = normalizedLaneId;
    }
    if (periodFilter) {
      // Parse period parameter to extract custom date range if present
      const parsedPeriod = this.parsePeriodParameter(periodFilter);

      // For custom date range, only send start_date and end_date (no filter_by)
      if (parsedPeriod.filterBy === 'custom' && parsedPeriod.startDate && parsedPeriod.endDate) {
        apiParams['start_date'] = parsedPeriod.startDate;
        apiParams['end_date'] = parsedPeriod.endDate;
        apiParams['filter_by'] = 'custom';
      } else {
        // For regular periods, send filter_by parameter
        apiParams['filter_by'] = parsedPeriod.filterBy;
      }
    }

    // Handle legacy filterValue parameter (for backward compatibility)
    if (filterValue && !locationId && !periodFilter) {
      // Check if it's a period filter (not a UUID-like location ID)
      if (this.isPeriodFilter(filterValue)) {
        apiParams['filter_by'] = filterValue;
      } else {
        // Assume it's a location ID
        apiParams['location_id'] = filterValue;
      }
    }

    // Add wash type filters to parameters
    this.addWashTypeFiltersToObject(apiParams, washTypeFilters);

    // Add status filters to parameters
    this.addStatusFiltersToObject(apiParams, statusFilters);

    // Add source filters to parameters
    this.addSourceFiltersToObject(apiParams, sourceFilters);

    // Add payment mode filters to parameters
    this.addPaymentModeFiltersToObject(apiParams, paymentModeFilters);

    // Add package filters to parameters
    this.addPackageFiltersToObject(apiParams, packageIds);

    // Add Discount Applied filter to parameters
    this.addDiscountAppliedFilterToObject(apiParams, discountApplied);

    const timezone = this.resolveOrdersTimezone(locationId);
    if (timezone) {
      apiParams['timezone'] = timezone;
    }

    const apiCall = useTunnelToken
      ? this.apiService.getWithTunnelToken(currentEndpoint, apiParams)
      : this.apiService.get(currentEndpoint, apiParams);

    return apiCall.pipe(
      map((response: any) => {
        console.log('API Response for page', page, ':', response);

        if (response.status && response.data) {
          // Handle new API structure with nested orders
          if (response.data.orders && Array.isArray(response.data.orders)) {
            const orders = this.mapApiOrdersToOrders(response.data.orders);
            // Get total from pagination or data.totalOrders
            const totalItems =
              response.pagination?.total || response.data.totalOrders || orders.length;
            console.log(
              'Total items calculated (nested orders):',
              totalItems,
              'from response fields:',
              {
                paginationTotal: response.pagination?.total,
                dataTotalOrders: response.data.totalOrders,
                ordersLength: orders.length,
              }
            );
            return { orders, totalItems: parseInt(String(totalItems), 10) };
          }

          // Handle old API structure (direct array)
          if (Array.isArray(response.data)) {
            const orders = this.mapApiOrdersToOrders(response.data);
            let totalItems =
              response.total ||
              response.totalItems ||
              response.count ||
              response.total_count ||
              response.data.length ||
              orders.length;

            // If we're using pagination but API doesn't return total, estimate it
            if (page > 1 && totalItems === orders.length) {
              // This might be the last page, so estimate total
              totalItems = (page - 1) * limit + orders.length;
            }

            console.log('Total items calculated (array):', totalItems);
            return { orders, totalItems };
          }
        }

        console.error('Unexpected response format:', response);
        throw new Error('Invalid response format');
      }),
      catchError(error => {
        console.error('API Error for endpoint:', currentEndpoint, error);
        if (remainingEndpoints.length > 0) {
          return this.tryEndpoints(
            remainingEndpoints,
            page,
            limit,
            searchTerm,
            filterValue,
            washTypeFilters,
            statusFilters,
            sourceFilters,
            locationId,
            periodFilter,
            useTunnelToken,
            laneId,
            paymentModeFilters,
            packageIds,
            discountApplied
          );
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Map display period names to API format
   */
  mapPeriodToApiFormat(period: string): string {
    // Check if period contains custom date range in format "Custom:startDate:endDate"
    if (period.startsWith('Custom:')) {
      // Return the full string - it will be parsed later in the API call
      return period;
    }

    const periodMap: Record<string, string> = {
      Today: 'today',
      Yesterday: 'yesterday',
      'This Week': 'week',
      'Last Week': 'last_week',
      'This Month': 'month',
      'Last Month': 'last_month',
      'This Year': 'year',
      'Last Year': 'last_year',
      Custom: 'custom',
      'All Time': '',
    };

    return periodMap[period] || period.toLowerCase();
  }

  /**
   * Parse period parameter that may include custom date range
   * Similar to analytics module implementation
   */
  /** Timezone for order APIs: explicit filter location, else default shop (not unrelated selected state). */
  private resolveOrdersTimezone(locationId?: string): string {
    if (locationId) {
      return this.locationService.getTimezoneForLocationId(locationId);
    }
    return this.locationService.getDefaultShopTimezone();
  }

  private parsePeriodParameter(period: string): {
    filterBy: string;
    startDate?: string;
    endDate?: string;
  } {
    // Handle custom date range format: "Custom:startDate:endDate"
    if (period.startsWith('Custom:')) {
      const sanitizedPeriod = period.replace(/^custom:/i, 'Custom:');
      const [, startDate = '', endDate = ''] = sanitizedPeriod.split(':');
      return {
        filterBy: 'custom',
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };
    }

    // Handle regular period values
    return { filterBy: period };
  }

  /**
   * Check if the filter value is a period filter (not a location ID)
   */
  private isPeriodFilter(filterValue: string): boolean {
    const periodValues = [
      'today',
      'yesterday',
      'week',
      'last_week',
      'month',
      'last_month',
      'year',
      'last_year',
      'all',
      'custom',
    ];
    return periodValues.includes(filterValue.toLowerCase());
  }

  /**
   * Map API orders to display format. Public for use by admin-dashboard and other consumers.
   */
  mapApiOrdersToOrders(apiOrders: ApiOrder[]): Order[] {
    return apiOrders.map(apiOrder => {
      // Derive wash count display for memberships when API provides wash_count metadata
      let washCount: string | undefined;
      washCount = OrderUtils.formatMembershipWashCountDisplay(apiOrder);
      if (washCount === 'N/A') {
        washCount = undefined;
      }

      const { vehicleInfo, licensePlate } = OrderUtils.mapVehicleFromApi(apiOrder);

      return {
        id: apiOrder.id,
        customerId: apiOrder.customer_id,
        orderId: apiOrder.order_id,
        // CWS-1883: a prepaid sale is badged from the book's real state, not the order row's.
        status:
          OrderUtils.mapPrepaidSaleStatus(apiOrder.prepaid_display_status) ??
          this.mapOrderStatus(apiOrder.order_status, apiOrder.payment_status),
        dateTime: apiOrder.appointment_time_local
          ? formatLocalDateTime(apiOrder.appointment_time_local)
          : this.formatDateTime(apiOrder.appointment_time),
        appointmentTime: apiOrder.appointment_time || undefined,
        customerName: apiOrder.customer_name,
        userName: apiOrder.user_name,
        userId: apiOrder.user_id,
        has_payment_issue: apiOrder.has_payment_issue,
        washType: OrderUtils.formatWashType(apiOrder.wash_type),
        washTypeValue: apiOrder.wash_type, // Store original API value for filtering
        addOnLabels: OrderUtils.formatAddOnKindLabels(apiOrder.addon_kinds),
        email: apiOrder.customer_email,
        phone: apiOrder.customer_contact,
        price: `$${apiOrder.final_price}`,
        listPrice: OrderUtils.formatListPriceDisplay(apiOrder),
        voucherCode: apiOrder.voucher_code || undefined,
        discount: `$${apiOrder.discount || '0.00'}`,
        discountId: String(apiOrder.discount_id ?? '').trim() || undefined,
        discountName: String(apiOrder.discount_name ?? '').trim() || undefined,
        source: apiOrder.source || 'Way.com',
        vehicleInfo,
        barCode: apiOrder.id,
        licensePlate,
        vehicleId: apiOrder.vehicle_id, // Added vehicle_id mapping
        branch: apiOrder.branch,
        packageName: apiOrder.package_name, // Added package_name mapping
        // CWS-1444: only carried when it actually differs, so a screen showing
        // it never has to compare two names itself.
        soldPackageName: apiOrder.plan_changed
          ? (apiOrder.sold_package_name ?? undefined)
          : undefined,
        planChanged: apiOrder.plan_changed ?? false,
        // Only when the plan changed: until then final_price IS the monthly
        // rate, and overriding it everywhere would change what every existing
        // membership order displays.
        currentPlanPrice:
          apiOrder.plan_changed &&
          apiOrder.current_plan_price !== null &&
          apiOrder.current_plan_price !== undefined
            ? `$${Number(apiOrder.current_plan_price).toFixed(2)}`
            : undefined,
        payment_method: apiOrder.payment_method || undefined,
        customerSource: apiOrder.customer_source ?? null,
        vin: apiOrder.vin || undefined,
        vinNumber: apiOrder.vin || undefined, // Map to both for backward compatibility
        lprId: apiOrder.lpr_entry_id || apiOrder.lpr_id || undefined,
        lprEntryId: apiOrder.lpr_entry_id || apiOrder.lpr_id || undefined,
        isFamilyPlanVehicle: apiOrder.is_family_plan ?? false,
        isFamilyPlanPackage: apiOrder.package_family_plan ?? false,
        isFamily: familyTagsOf(apiOrder).isFamily,
        isFamilyPrimary: familyTagsOf(apiOrder).isPrimary,
        membershipStatus: apiOrder.membership_subscription_status ?? null,
        membershipCancelAtPeriodEnd: apiOrder.membership_cancel_at_period_end ?? false,
        membershipCurrentPeriodEnd: apiOrder.membership_current_period_end ?? null,
        fleetId: apiOrder.fleet_id ?? null,
        fleetName: apiOrder.fleet_name ?? null,
        fleetVehicleType: apiOrder.fleet_vehicle_type ?? null,
        customerType: apiOrder.customer_type ?? null,
        washCount,
        laneName: OrderUtils.mapLaneNameFromApi(apiOrder),
        locationTimezone: apiOrder.location_timezone?.trim() || undefined,
        // CWS-1777 / CWS-1771: carried through untouched; the table renders them.
        paymentStatusDisplay: apiOrder.payment_status_display ?? null,
        paidTotal: toAmountOrNull(apiOrder.paid_total),
        refundedTotal: toAmountOrNull(apiOrder.refunded_total),
        paymentStatus: apiOrder.payment_status ?? null,
        cancellation: apiOrder.cancellation ?? null,
        locationId: apiOrder.location_id ?? null,
        createdAt: apiOrder.created_at_local
          ? formatLocalDateTime(apiOrder.created_at_local)
          : apiOrder.created_at
            ? this.formatDateTime(apiOrder.created_at)
            : undefined,
      };
    });
  }

  private mapOrderStatus(orderStatus: string, paymentStatus: string): OrderStatus {
    return OrderUtils.mapOrderStatus(orderStatus, paymentStatus);
  }

  /**
   * Format API datetime string to "Oct 17 2025, 3.49 PM IST" format
   */
  private formatDateTime(dateTimeString: string): string {
    return formatDateTimeWithTimezone(dateTimeString);
  }

  /**
   * Get order statistics from API
   */
  getOrderStatistics(locationId?: string): Observable<OrderStats[]> {
    const params: Record<string, string> = {};
    const timezone = this.resolveOrdersTimezone(locationId);
    if (timezone) {
      params['timezone'] = timezone;
    }
    if (locationId) {
      params['location_id'] = locationId;
    }
    return this.apiService.get<ApiOrderStatistics>('order/statistics', params).pipe(
      map(response => {
        if (response.status && response.data) {
          return mapApiStatisticsToOrderStats(response.data);
        }
        throw new Error('Invalid statistics response format');
      }),
      catchError(error => {
        console.error('Error fetching order statistics:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Sort orders by order ID
   */
  sortOrdersByOrderId(orders: Order[], direction: 'asc' | 'desc' = 'asc'): Order[] {
    return [...orders].sort((a, b) => {
      const numA = parseInt(a.orderId.replace(/[^0-9]/g, ''), 10) || 0;
      const numB = parseInt(b.orderId.replace(/[^0-9]/g, ''), 10) || 0;

      return direction === 'asc' ? numA - numB : numB - numA;
    });
  }

  /**
   * Chronological instant for an order: API UTC first, then location wall-clock,
   * then browser-local parse of the display string. Invalid values become 0.
   */
  private orderDateTimeMs(order: Order): number {
    if (order.appointmentTime) {
      const fromIso = Date.parse(order.appointmentTime);
      if (Number.isFinite(fromIso)) {
        return fromIso;
      }
    }
    const fromDisplay = parseOrdersDisplayDateTime(
      order.dateTime,
      order.locationTimezone
    ).getTime();
    return Number.isFinite(fromDisplay) ? fromDisplay : 0;
  }

  /**
   * Sort orders by actual chronology (not browser-local wall-clock of the display string).
   */
  sortOrdersByDateTime(orders: Order[], direction: 'asc' | 'desc' = 'asc'): Order[] {
    return [...orders].sort((a, b) => {
      const safeA = this.orderDateTimeMs(a);
      const safeB = this.orderDateTimeMs(b);
      return direction === 'asc' ? safeA - safeB : safeB - safeA;
    });
  }

  /**
   * Paginate orders
   */
  paginateOrders(orders: Order[], page: number, limit: number): Order[] {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return orders.slice(startIndex, endIndex);
  }

  /**
   * Get total pages for pagination
   */
  getTotalPages(totalItems: number, itemsPerPage: number): number {
    return Math.ceil(totalItems / itemsPerPage);
  }

  /**
   * Get pagination info string
   */
  getPaginationInfo(currentPage: number, itemsPerPage: number, totalItems: number): string {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);
    return `Items ${startItem} - ${endItem} of ${totalItems}`;
  }

  /**
   * Get CSS class for order status
   */
  getStatusClass(status: OrderStatus): string {
    return `status-${status.color}`;
  }

  /**
   * Get CSS class for wash type
   */
  getWashTypeClass(washType: string, washTypeValue?: string): string {
    const color = OrderUtils.getWashTypeColor(washTypeValue || washType);
    if (color) {
      return `wash-type-${color}`;
    }
    const type = this.washTypes.find(t => t.label === washType);
    return type ? `wash-type-${type.color}` : '';
  }

  /**
   * Get available order statuses
   */
  getOrderStatuses(): OrderStatus[] {
    return [...this.orderStatuses];
  }

  /**
   * Get available wash types
   */
  getWashTypes(): WashType[] {
    return [...this.washTypes];
  }

  /**
   * Cancel an order
   */
  cancelOrder(orderId: string, reason?: string): Observable<any> {
    const payload = {
      cancelled_by: 'admin',
      cancellation_reason: reason || 'Canceled by admin',
    };

    return this.apiService.put(`order/cancel/${orderId}`, payload);
  }

  /**
   * Update an order with new vehicle details
   */
  updateOrder(orderId: string, vehicleId: string): Observable<any> {
    const payload = {
      vehicle_id: vehicleId,
    };

    return this.apiService.put(`order/${orderId}`, payload);
  }

  /**
   * Correct the plate recorded on an order (CWS-1400).
   *
   * Orders keep the plate they were taken with and the Orders page prefers that over the
   * live vehicle plate, so fixing the vehicle alone leaves the order showing the old one.
   */
  updateOrderPlateSnapshot(orderId: string, licensePlate: string, state?: string): Observable<any> {
    const payload: Record<string, string> = { license_plate_snapshot: licensePlate };
    if (state) payload['state_snapshot'] = state;
    return this.apiService.put(`order/${orderId}`, payload);
  }

  /**
   * Get order details by ID
   */
  getOrderById(orderId: string): Observable<Order> {
    return this.apiService.get(`order/${orderId}`).pipe(
      map((response: any) => {
        if (response.status && response.data) {
          const payload = response.data;
          const apiOrder = payload?.order ?? payload;
          if (!apiOrder || typeof apiOrder !== 'object') {
            throw new Error('Order not found');
          }
          return this.mapApiOrdersToOrders([apiOrder])[0];
        }
        throw new Error('Order not found');
      })
    );
  }

  /**
   * Add wash type filters to params object
   * Uses wash_type for single selection, wash_types for multiple selections
   */
  private addWashTypeFiltersToObject(
    params: Record<string, any>,
    washTypeFilters?: WashTypeFilters
  ): void {
    if (!washTypeFilters) return;

    const washTypes: string[] = [];
    if (washTypeFilters.singleWash) {
      washTypes.push('single_wash');
    }
    if (washTypeFilters.unlimitedWash) {
      washTypes.push('monthly_membership');
    }
    if (washTypeFilters.prepaidWash) {
      // Both sides of a wash book — purchase and redemptions. See PREPAID_FILTER_WASH_TYPES.
      washTypes.push(...PREPAID_FILTER_WASH_TYPES);
    }
    if (washTypeFilters.rewash) {
      washTypes.push('rewash');
    }

    if (washTypes.length > 0) {
      if (washTypes.length === 1) {
        const [singleWash] = washTypes;
        params['wash_type'] = singleWash;
      } else {
        // Multiple selections: use wash_types (plural)
        params['wash_types'] = washTypes.join(',');
      }
    }
  }

  /**
   * Add status filters to params object
   */
  private addStatusFiltersToObject(
    params: Record<string, any>,
    statusFilters?: StatusFilters
  ): void {
    if (!statusFilters) return;

    const statuses: string[] = [];
    if (statusFilters.completed) statuses.push('completed');
    if (statusFilters.pending) statuses.push('pending');
    if (statusFilters.cancelled) statuses.push('cancelled');
    if (statusFilters.scheduled) statuses.push('scheduled');
    if (statusFilters.inProgress) statuses.push('inprogress');

    if (statuses.length > 0) {
      params['order_status'] = statuses.join(',');
    }
  }

  /**
   * Add source filters to params object
   */
  private addSourceFiltersToObject(
    params: Record<string, any>,
    sourceFilters?: { wayCom: boolean; scanAndGo: boolean; wayApp: boolean; website: boolean }
  ): void {
    if (!sourceFilters) return;

    const sources: string[] = [];
    if (sourceFilters.wayCom) sources.push('way_web');
    if (sourceFilters.scanAndGo) sources.push('scan_and_go');
    if (sourceFilters.wayApp) sources.push('way_app');
    if (sourceFilters.website) sources.push('web');

    if (sources.length > 0) {
      params['source'] = sources.join(',');
    }
  }

  /**
   * Add payment mode filters to params object (comma-separated `payment_method`).
   */
  private addPaymentModeFiltersToObject(
    params: Record<string, any>,
    paymentModeFilters?: PaymentModeFilters
  ): void {
    if (!paymentModeFilters) return;

    const modes = mapPaymentModeFiltersToApiValues(paymentModeFilters);
    if (modes.length > 0) {
      params['payment_method'] = modes.join(',');
    }
  }

  /**
   * Add package filters to params object (comma-separated `package_ids`).
   */
  private addPackageFiltersToObject(params: Record<string, any>, packageIds?: string[]): void {
    if (!packageIds?.length) return;
    const ids = packageIds.map(id => String(id).trim()).filter(Boolean);
    if (ids.length > 0) {
      params['package_ids'] = ids.join(',');
    }
  }

  /**
   * Add Discount Applied filter (`discount_applied`: none | any | UUID).
   * Omits the param for `all` / empty so the API returns unrestricted results.
   */
  private addDiscountAppliedFilterToObject(
    params: Record<string, any>,
    discountApplied?: string
  ): void {
    const value = String(discountApplied ?? '')
      .trim()
      .toLowerCase();
    if (!value || value === 'all') {
      return;
    }
    // Preserve original UUID casing for the API; only special tokens are lowercased above.
    const raw = String(discountApplied ?? '').trim();
    params['discount_applied'] = value === 'none' || value === 'any' ? value : raw;
  }

  /**
   * Add wash type filters to URL parameters (legacy - kept for backward compatibility)
   * Uses wash_type for single selection, wash_types for multiple selections
   */
  private addWashTypeFilters(params: URLSearchParams, washTypeFilters?: WashTypeFilters): void {
    if (!washTypeFilters) return;

    const washTypes: string[] = [];
    if (washTypeFilters.singleWash) {
      washTypes.push('single_wash');
    }
    if (washTypeFilters.unlimitedWash) {
      washTypes.push('monthly_membership');
    }
    if (washTypeFilters.prepaidWash) {
      washTypes.push(...PREPAID_FILTER_WASH_TYPES);
    }
    if (washTypeFilters.rewash) {
      washTypes.push('rewash');
    }

    if (washTypes.length > 0) {
      if (washTypes.length === 1) {
        // Single selection: use wash_type (singular)
        params.set('wash_type', washTypes[0]);
      } else {
        // Multiple selections: use wash_types (plural)
        params.set('wash_types', washTypes.join(','));
      }
    }
  }

  /**
   * Add status filters to URL parameters (legacy - kept for backward compatibility)
   */
  private addStatusFilters(params: URLSearchParams, statusFilters?: StatusFilters): void {
    if (!statusFilters) return;

    const statuses: string[] = [];
    if (statusFilters.completed) statuses.push('completed');
    if (statusFilters.pending) statuses.push('pending');
    if (statusFilters.cancelled) statuses.push('cancelled');
    if (statusFilters.scheduled) statuses.push('scheduled');
    if (statusFilters.inProgress) statuses.push('inprogress');

    if (statuses.length > 0) {
      params.set('order_status', statuses.join(','));
    }
  }

  /**
   * Add source filters to URL parameters (legacy - kept for backward compatibility)
   */
  private addSourceFilters(
    params: URLSearchParams,
    sourceFilters?: { wayCom: boolean; scanAndGo: boolean; wayApp: boolean; website: boolean }
  ): void {
    if (!sourceFilters) return;

    const sources: string[] = [];
    if (sourceFilters.wayCom) sources.push('way_web');
    if (sourceFilters.scanAndGo) sources.push('scan_and_go');
    if (sourceFilters.wayApp) sources.push('way_app');
    if (sourceFilters.website) sources.push('web');

    if (sources.length > 0) {
      params.set('source', sources.join(','));
    }
  }

  /**
   * Add payment mode filters to URL parameters (legacy - kept for backward compatibility)
   */
  private addPaymentModeFilters(
    params: URLSearchParams,
    paymentModeFilters?: PaymentModeFilters
  ): void {
    if (!paymentModeFilters) return;

    const modes = mapPaymentModeFiltersToApiValues(paymentModeFilters);
    if (modes.length > 0) {
      params.set('payment_method', modes.join(','));
    }
  }

  /**
   * Active payment modes for the Orders Payment Mode filter (GET `user/payment-modes`).
   * Built-in Tap to Pay / Cash / Card / Payment Link stay hardcoded; callers merge the rest.
   */
  getPaymentModesForFilter(): Observable<
    { id?: string | number; name?: string; is_active?: boolean }[]
  > {
    return this.apiService
      .get<{ id?: string | number; name?: string; is_active?: boolean }[]>('user/payment-modes')
      .pipe(
        map(response => (Array.isArray(response?.data) ? response.data : [])),
        catchError(() => of([]))
      );
  }
}
